import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    Button,
    IconButton,
    useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import Axios from "axios";
import { useSignOut } from "react-auth-kit";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { tokens } from "../../theme";
import { getAuthHeader } from "../../utils/authToken";
import { exportElementToPdf } from "../../utils/exportElementPdf";
import Loading from "../../components/Loading";
import UploadToClassMaterialModal from "./UploadToClassMaterialModal";
import EttReportView from "./EttReportView";

const ETTReportDetail = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const signOut = useSignOut();
    const navigate = useNavigate();
    const location = useLocation();
    const { userId, testId } = useParams();
    const pageRef = useRef();

    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const localStudent = location.state?.localStudent || null;
    const [uploadModalOpen, setUploadModalOpen] = useState(false);

    const handleLogout = () => {
        signOut();
        localStorage.removeItem("userName");
        localStorage.removeItem("who");
        navigate("/signin");
    };

    useEffect(() => {
        const fetchReport = async () => {
            try {
                const response = await Axios.get(
                    `${process.env.REACT_APP_URL}/api/v1/ett-reports/users/${userId}/tests/${testId}/report`,
                    { headers: { Authorization: getAuthHeader() } }
                );
                setReport(response.data);
            } catch (error) {
                if (error.response?.status === 401) {
                    handleLogout();
                } else {
                    console.error("Fetch report error:", error);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, testId]);

    const generatePDF = () =>
        exportElementToPdf(
            pageRef.current,
            `ETT_Report_${report.student?.first_name || ""}_${report.student?.last_name || ""}_${report.test_title}.pdf`
        );

    localStorage.setItem("selectedMenu", "ettReports");

    if (loading) return <Loading />;

    if (!report) {
        return (
            <Box m="20px">
                <IconButton onClick={() => navigate('/admin/ettReports', { state: location.state })}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography color={colors.grey[300]} mt="20px">
                    Report not found.
                </Typography>
            </Box>
        );
    }

    return (
        <Box m="20px">
            {/* Action bar */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb="20px">
                <Box display="flex" alignItems="center" gap="10px">
                    <IconButton onClick={() => navigate('/admin/ettReports', { state: location.state })}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h4" fontWeight="bold">
                        ETT Exam Report
                    </Typography>
                </Box>
                <Box display="flex" gap="10px">
                    <Button variant="contained" color="primary" startIcon={<DownloadIcon />} onClick={generatePDF}>
                        Download PDF
                    </Button>
                    {localStudent && (
                        <Button variant="contained" color="secondary" startIcon={<UploadIcon />} onClick={() => setUploadModalOpen(true)}>
                            Upload to Class Material
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Shared printable report body (also used by the prep-program exam-matrix modal). */}
            <EttReportView ref={pageRef} report={report} />

            {localStudent && (
                <UploadToClassMaterialModal
                    open={uploadModalOpen}
                    onClose={() => setUploadModalOpen(false)}
                    report={report}
                    localStudent={localStudent}
                    ettUserId={userId}
                    testId={testId}
                />
            )}
        </Box>
    );
};

export default ETTReportDetail;
